/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */

import { AgentIcon, Badge, Button, Chip, DateTimeSelector, Grid, IconButton, PresetsType, ProfileCard, Select, SelectChangeEvent, SelectValueType, SxProps, Theme, Tooltip, TopBar, Box } from '@h9/ui-lib';
import React, { memo, useCallback, useEffect, useMemo, useState, CSSProperties } from "react";
import { useMobile } from "~/hooks/useMobile";
import { authenticationService, userService } from "~/services";
import { Icons } from "~/components/GraphicalResources";
import EditProfile from "./UserProfile/EditProfile";
import ApiTokenDialog from "~/views/adminstration/Tabs/apiToken/ApiTokenDialog";
import LocationOn from "@mui/icons-material/LocationOn";
import { API_DOCS, DASHBOARD, DOCUMENTATION, IT_DASHBOARD, SUPPORT } from "~/constants/routes";
import { Types } from "~/constants/types";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {
  selectEndTime,
  selectStartTime,
  selectTimeInterval,
  dashboardActions,
  selectCurrentServiceController,
} from "~/store/context/dashboardSlice";
import { authUser } from "~/store/context/authSlice";
import { siteActions, siteCurrentDashboard, siteData } from "~/store/context/siteSlice";
import { text } from "~/components/TextResources";
import { getProvisionedRole } from "~/helpers/utils";
import moment from "moment";
import { utilityActions } from "~/store/context/utilitySlice";
import { useHistory, useLocation } from "react-router-dom";
import { settingsAssistantEnabled, settingsDashboardInsightsEnabled, settingsSystemDashboardConsoleEnabled } from "~/store/context/settingsSlice";
import NotificationBellIcon from "~/components/Notifications/NotificationBellIcon";
import { getImageSrc } from "~/services/image-service";
import { GoingOutOfSupportChip } from "./GoingOutOfSupportChip";

export type TopBarType = "primary" | "secondary";

export type TopBarPropType = {
  backgroundColor?: CSSProperties["backgroundColor"];
  NotificationComponent?: React.ComponentType;
  subtitle?: string;
  title: string;
  type?: TopBarType;
  bodyId?: string;
};

const dateRanges: PresetsType[] = [
  {
    label: "Last 1 day",
    value: "1day",
  },
  {
    label: "Last 1 week",
    value: "1week",
  },
  {
    label: "Last 1 month",
    value: "1month",
  },
  {
    label: "Last 3 months",
    value: "3month",
  },
  {
    label: "Last 6 months",
    value: "6month",
  },
  {
    label: "Last 1 year",
    value: "1year",
  },
  {
    label: "Custom",
    value: 'custom'
  },
];

export function TopBarNew({ subtitle, title, bodyId }: TopBarPropType) {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isApiTokenOpen, setIsApiTokenOpen] = React.useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useMobile();

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleEditProfileOpen = () => {
    setIsEditOpen(true);
    handleClose();
  };

  const handleEditProfileClose = () => {
    setIsEditOpen(false);
  };

  const currentUserInfo = useAppSelector(authUser) ?? userService.currentUserInfo;
  const provisionedRole = getProvisionedRole(currentUserInfo);
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const profileImageId = currentUserInfo?.profilePicture?.imageId;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!profileImageId) {
        setProfileImage(undefined);
        return;
      }
      const url = await getImageSrc(profileImageId);
      if (!cancelled) setProfileImage(url);
    };
    load();
    return () => { cancelled = true; };
  }, [profileImageId]);
  useEffect(() => {
    if (bodyId) {
      const bodyBlock = document.getElementById(bodyId) as HTMLElement;
      bodyBlock.onscroll = function () {
        if (bodyBlock.scrollTop > 50) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      };
    }
  }, [bodyId]);

  return (
    <TopBar
      title={title}
      onMobileSideDrawerOpen={() => dispatch(utilityActions.setMobileSideDrawerOpen(true))}
      subtitle={subtitle}
      userName={authenticationService.currentUserValue?.userName}
      role={provisionedRole}
      profilePicture={profileImage}
      profileCard={
        <ProfileCard
          profile={{
            name: authenticationService.currentUserValue?.userName ?? "",
            role: provisionedRole,
            email: authenticationService.currentUserValue?.email ?? "",
            profilePicture: profileImage ?? '',
          }}
          menuOptions={[
            {
              icon: Icons.edit,
              text: text.editProfile!,
              onClick: handleEditProfileOpen,
            },
            {
              icon: Icons.vpnKey,
              text: "API Token",
              onClick: () => {
                setAnchorEl(null);
                setIsApiTokenOpen(true);
              },
            },
            {
              icon: Icons.code,
              text: text.apiDocs!,
              onClick: () => {
                window.open(API_DOCS, "_blank");
              },
            },
            {
              icon: Icons.documentIcon,
              text: text.documentation!,
              onClick: () => {
                const existingWindow = window.open(DOCUMENTATION, "documentation");
                if (existingWindow) {
                  existingWindow.focus();
                }
              },
            },
            {
              icon: Icons.supportIcon,
              text: text.support!,
              onClick: () => {
                const existingWindow = window.open(SUPPORT, "support");
                if (existingWindow) {
                  existingWindow.focus();
                }
              },
            },
            {
              icon: Icons.logout,
              text: text.logout!,
              style: commonStyles.logoutItem,
              onClick: () => {
                authenticationService.logout();
              },
            },
          ]}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      }
      setAnchorEl={setAnchorEl}
      style={{ boxShadow: scrolled ? "0 2px 4px 0 rgba(0,0,0,.2);" : null }}
    >
      <TopBarChildren isMobile={isMobile} />
      <GoingOutOfSupportChip />
      <VirtualEngineerBtn isMobile={isMobile} />
      <NotificationBellIcon />
      <InsightsIcons />
      {/* {NotificationComponent ? <NotificationComponent /> : <AlertBadgeRegular fontSize={20} />} */}
      <EditProfile state={isEditOpen} handleClose={handleEditProfileClose} />
      <ApiTokenDialog open={isApiTokenOpen} onClose={() => setIsApiTokenOpen(false)} />
    </TopBar>
  );
}

const TopBarChildren = ({ isMobile }: { isMobile: boolean }) => {
  const location = useLocation();
  return (
    <Grid container sx={commonStyles.rightContainer}>
      {location.pathname === DASHBOARD && (
        <Grid container gap={isMobile ? "8px" : "16px"} width="fit-content">
          {!isMobile && <TimeSelection />}
          <Box sx={{ width: "auto" }}>
          <SiteSelection />
          </Box>
        </Grid>
      )}
      {/* {NotificationComponent ? <NotificationComponent /> : <AlertBadgeRegular fontSize={20} />} */}
    </Grid>
  );
};

const commonStyles = {
  rightContainer: {
    width: "fit-content",
    alignItems: "center",
    gap: {
      xs: "8px",
      sm: "16px"
    },
    justifyContent: "flex-end",
    flex: 1,
  },
  logoutItem: {
    color: "#D32F2F !important",
    gap: "8px",
    "& .MuiSvgIcon-root": {
      width: "16px",
      height: "16px",
      color: "#DF3D2D",
    },
  },
  tooltip: {
    bgcolor: "#292B2F",
    marginLeft: "8px !important",
    padding: "8px",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    color: "#F7F7F7",
  }
} satisfies Record<string, SxProps<Theme>>;

function SiteSelection() {
  const dispatch = useAppDispatch();
  const sites = useAppSelector(siteData);
  const selectedSite = useAppSelector(siteCurrentDashboard);
  const [selectedFilter, setSelectedFilter] = useState<string>(selectedSite?.id || Types.all);

  const siteOptions = useMemo(() => {
    const allSiteOption = {
      id: Types.all,
      label: text.allSites!,
      value: Types.all,
      icon: <LocationOn sx={{ fontSize: "1rem" }} />,
    };

    return [
      allSiteOption,
      ...sites.map(({ id, name }, idx) => ({
        id: id || `site-option-${idx}`,
        label: name,
        value: id || `site-option-val-${idx}`,
        icon: <LocationOn sx={{ fontSize: "1rem" }} />,
      })),
    ];
  }, [sites]);

  const handleFilterChange = (event: SelectChangeEvent<SelectValueType>) => {
    const selectedSiteId = event.target.value as string;
    const selectedSite = sites.find((site) => site.id === selectedSiteId);
    dispatch(siteActions.setDashboardSelectedSite(selectedSite || null));
    setSelectedFilter(event.target.value as string);
  };

  return (
    <Select
      id="site-selectory"
      variant="filled"
      value={selectedFilter}
      onChange={handleFilterChange}
      options={siteOptions}
      sx={{ marginRight: "8px" }}
    />
  );
}

const TimeSelection = memo(() => {
  const dispatch = useAppDispatch();
  const interval = useAppSelector(selectTimeInterval);

  const startTime = useAppSelector(selectStartTime);
  const endTime = useAppSelector(selectEndTime);

  const handleDateChange = useCallback((startTime: number, endTime: number, range: string): void => {
    dispatch(dashboardActions.setStartTime(startTime));
    dispatch(dashboardActions.setEndTime(endTime));

    if (dateRanges.find(el => el.value === range)) {
      dispatch(dashboardActions.setTimeInterval({ interval: range, rounding: 0 }));
    } else {
      dispatch(dashboardActions.setTimeInterval({ interval: 'custom' }));
    }
  }, [dispatch]);

  return (
    <DateTimeSelector
      startTime={startTime}
      endTime={endTime}
      hideTime={true}
      deltaChangePercent={1}
      minTime={moment().subtract(1, 'year').unix()}
      interval={interval}
      options={dateRanges}
      onChange={handleDateChange}
    />
  );
});

function InsightsIcons() {
  const history = useHistory();

  const dashboardInsightEnabled = useAppSelector(settingsDashboardInsightsEnabled);
  const dashboardConsoleEnabled = useAppSelector(settingsSystemDashboardConsoleEnabled);
  const notificationCount = useAppSelector(selectCurrentServiceController)?.insightsSummary?.activeCount;
  const handleNotificationOpen = () => {
    history.push(IT_DASHBOARD + "?tab=insights");
  };

  if (!dashboardConsoleEnabled) {
    return null;
  }

  if (!dashboardInsightEnabled) {
    return null;
  }

  return (
    <Tooltip title="Insights"
      placement="bottom"
      key="insights"
      aria-label="insights"
      slotProps={
        {
          tooltip: {
            sx: commonStyles.tooltip
          }
        }
      }
    >
    <IconButton onClick={handleNotificationOpen} >
      <Badge badgeContent={notificationCount} color="success">
        <Icons.insights color="primary" />
      </Badge>
    </IconButton>
    </Tooltip>
  );
}

function VirtualEngineerBtn({ isMobile }: { isMobile: boolean }) {
  const dispatch = useAppDispatch();

  const enabled = useAppSelector(settingsAssistantEnabled);

  const handleAIOpen = () => {
    dispatch(utilityActions.toggleChatBot());
  };

  if (!enabled) {
    return null;
  }

  return (
    <Button title="ALT + K / Option + K to open Virtual Engineer"
      sx={{
        height: isMobile ? '1.75rem' : '2rem',
        fontWeight: '600',
        fontSize: isMobile ? '11px' : '13px',
        px: isMobile ? "6px" : "10px",
        minWidth: isMobile ? 'auto' : 'fit-content'
      }}
      startIcon={<AgentIcon color='#226a58' size={isMobile ? 20 : 24} />}
      color="primary"
      variant="outlined"
      onClick={handleAIOpen}>
      {!isMobile && text.askAI} &nbsp;
      {!isMobile && <Chip variant="outlined" size="small" sx={{ mr :2}} color="primary" label={
        <span style={{ display: 'flex', alignItems: 'center' }}>
          AI
          <AutoAwesomeOutlinedIcon
            style={{ fontSize: "small", rotate : '-180deg', marginLeft: '4px' }}
            titleAccess="AutoDelete"
          />
        </span>
      } />}
    </Button>
  );
}
